import { z } from 'zod';
import { getApiOrigin } from '../shared/api-client';
import { getPersistentJson } from '../vrs-auth/storage';
const id = z.string().min(1).max(120);
const timestamp = z.string().refine(value => Number.isFinite(Date.parse(value)));
const text = z.string().nullable().optional();
const noteSchema = z.object({ id, contact_id: id, author_id: id, content: z.string().min(1), created_at: timestamp, updated_at: timestamp });
const timelineSchema = z.object({ type: z.enum(['call', 'missed_call', 'voicemail', 'note']), id, timestamp, data: z.record(z.string(), z.unknown()) });
const groupSchema = z.object({ id, name: z.string() });
const contactSchema = z.object({
    id, client_id: id.nullable().optional(), name: z.string().trim().min(1), email: text, phone_number: text,
    contact_handle: text, handle: text, organization: text, notes: text, avatar_color: text,
    is_favorite: z.union([z.boolean(), z.literal(0), z.literal(1)]).nullable(), linked_client_id: id.nullable().optional(),
    merged_into: id.nullable().optional(), group_ids: text, group_names: text,
    last_call_date: timestamp.nullable().optional(), created_at: timestamp.nullable().optional(), updated_at: timestamp.nullable().optional(),
    groups: z.array(groupSchema).optional(), timeline: z.array(timelineSchema).optional(), notesList: z.array(noteSchema).optional()
});
function currentClientId() {
    const user = z.object({ id: id.optional(), userId: id.optional() }).safeParse(getPersistentJson('vrs_user_info'));
    const clientId = user.success ? user.data.id || user.data.userId : undefined;
    if (!clientId)
        throw new Error('Session expired. Sign in again.');
    return clientId;
}
export function contactDetailScope() {
    const origin = getApiOrigin();
    if (!origin)
        throw new Error('Session expired. Sign in again.');
    return JSON.stringify([origin, currentClientId()]);
}
export function checkedContactDetail(value, expectedId) {
    const result = z.object({ contact: contactSchema }).safeParse(value);
    if (!result.success || result.data.contact.id !== expectedId)
        throw new Error('Contact details could not be confirmed. Try again.');
    const row = result.data.contact, directory = expectedId.startsWith('platform-');
    if ((!directory && row.client_id !== currentClientId()) || (directory && row.linked_client_id !== expectedId.slice(9)))
        throw new Error('The contact identity did not match. Refresh Contacts.');
    if (!directory && (!row.timeline || !row.notesList || !row.groups))
        throw new Error('Contact activity or notes could not be confirmed. Try again.');
    if (row.notesList?.some(note => note.contact_id !== expectedId))
        throw new Error('The notes did not match this contact. Try again.');
    return { contact: mapContact(row), timeline: row.timeline || [], notes: row.notesList || [] };
}
function mapContact(row) {
    return {
        id: row.id, client_id: row.client_id ?? null, name: row.name, email: row.email ?? null, phone_number: row.phone_number ?? null,
        contact_handle: row.contact_handle ?? row.handle ?? null, organization: row.organization ?? null, notes: row.notes ?? null,
        avatar_color: row.avatar_color ?? null, is_favorite: row.is_favorite ? 1 : 0, linked_client_id: row.linked_client_id ?? null,
        merged_into: row.merged_into ?? null, group_ids: row.groups?.map(group => group.id).join(',') ?? row.group_ids ?? null,
        group_names: row.groups?.map(group => group.name).join(',') ?? row.group_names ?? null, groups: row.groups,
        last_call_date: row.last_call_date ?? null, created_at: row.created_at ?? null, updated_at: row.updated_at ?? null
    };
}
export function checkedContactList(value) {
    const result = z.object({ contacts: z.array(contactSchema) }).safeParse(value);
    if (!result.success)
        throw new Error('Contacts could not be confirmed. Try again.');
    const ownerId = currentClientId(), seen = new Set();
    for (const row of result.data.contacts) {
        const directory = row.id.startsWith('platform-'), target = directory ? row.id.slice(9) : row.id;
        if (!z.string().uuid().safeParse(target).success || seen.has(row.id) || row.merged_into
            || (directory ? row.linked_client_id !== target : row.client_id !== ownerId)) {
            throw new Error('Contact identities could not be confirmed. Try again.');
        }
        seen.add(row.id);
    }
    return { contacts: result.data.contacts.map(mapContact) };
}
/**
 * A saved contact's dated private notes and activity (GET /api/contacts/:id
 * attaches notesList and timeline), for the native detail screen (plan #41).
 * null when the reply has neither: a directory contact, or an older server.
 *
 * @throws When they are there but cannot be confirmed for this contact.
 */
export function checkedContactActivity(value, contactId) {
    const contact = z.object({ contact: z.object({ notesList: z.unknown(), timeline: z.unknown() }).partial() }).safeParse(value);
    if (!contact.success || (contact.data.contact.notesList === undefined && contact.data.contact.timeline === undefined))
        return null;
    const result = z.object({ notesList: z.array(noteSchema), timeline: z.array(z.unknown()) }).safeParse(contact.data.contact);
    if (!result.success || result.data.notesList.some(note => note.contact_id !== contactId)) {
        throw new Error('Notes could not load. Try again.');
    }
    // An activity row the app cannot read (a call that never started has no
    // time) is left out; the notes themselves are all-or-nothing.
    return {
        notes: result.data.notesList,
        timeline: result.data.timeline.flatMap(item => {
            const row = timelineSchema.safeParse(item);
            return row.success ? [row.data] : [];
        })
    };
}
export function checkedAddedNote(value, contactId) {
    const result = z.object({ note: noteSchema }).safeParse(value);
    if (!result.success || result.data.note.contact_id !== contactId || result.data.note.author_id !== currentClientId())
        throw new Error('Note save not confirmed. Refresh notes.');
    return result.data.note;
}
export function requireNoteSuccess(value) {
    if (!z.object({ success: z.literal(true) }).safeParse(value).success)
        throw new Error('Note change not confirmed. Refresh notes.');
}
/** A 404 on retry is success only when this account has a matching trash record. */
export function requireTrashedContact(value, expectedId) {
    const result = z.object({ contacts: z.array(z.unknown()) }).safeParse(value);
    const directory = expectedId.startsWith('platform-');
    const trashed = z.object({
        id: z.string().uuid(), client_id: id, linked_client_id: z.string().uuid().nullable(),
        deleted_at: timestamp, merged_into: z.null()
    });
    const ownerId = currentClientId();
    if (result.success && result.data.contacts.some(value => {
        const row = trashed.safeParse(value);
        return row.success && row.data.client_id === ownerId
            && (directory ? row.data.linked_client_id === expectedId.slice(9) : row.data.id === expectedId);
    }))
        return;
    throw new Error('Deletion not confirmed. Refresh Contacts.');
}
